import {
  addProductToCart,
  getCustomerCarts,
  removeProductFromCart,
  updateCartProductQuantity,
} from "../api/cart";
import {
  addProductToPendingOrder,
  getOrderDetails,
  removeProductToPendingOrder,
  updateQuantityProductToPendingOrder,
} from "../api/orders";
import {
  setAlert,
  setOptimisticCartProductQuantity,
  setOptimisticOrderProductQuantity,
  updateCart,
  reconcileSelectedCart,
  updateOrder,
  updateRequestIsOrder,
} from "../reducers/userConfigSlice";
import { store } from "../store";

const STORAGE_KEY = "delibran-product-sync-queue-v1";
const SYNC_DELAY = 500;
const paused = new Set();

const entries = new Map();
const timers = new Map();
const running = new Map();
const errors = new Map();
const listeners = new Set();
let snapshotVersion = 0;

const canUseStorage = () =>
  typeof window !== "undefined" && Boolean(window.localStorage);

const persist = () => {
  if (!canUseStorage()) return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(Array.from(entries.values())),
    );
  } catch (error) {
    console.error(
      "[ProductSyncQueue] No se ha podido persistir la cola:",
      error,
    );
  }
};

const restore = () => {
  if (!canUseStorage()) return;
  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "[]");
    if (Array.isArray(saved))
      saved.forEach((entry) => {
        if (!entry?.key || !entry.resourceKey) return;
        entries.set(entry.key, entry);
        paused.add(entry.resourceKey);
        errors.set(
          entry.resourceKey,
          "Cambios conservados de una sesión anterior. Revísalos antes de continuar.",
        );
      });
  } catch (error) {
    console.error(
      "[ProductSyncQueue] No se ha podido restaurar la cola:",
      error,
    );
  }
};

restore();

if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", (event) => {
    if (entries.size === 0 && running.size === 0) return;
    event.preventDefault();
    event.returnValue = "";
  });
}

const emit = () => {
  snapshotVersion += 1;
  listeners.forEach((listener) => listener());
};

const getResourceEntries = (resourceKey) =>
  Array.from(entries.values()).filter(
    (entry) => entry.resourceKey === resourceKey,
  );

const updateResourceEntries = (resourceKey, changes) => {
  getResourceEntries(resourceKey).forEach((entry) =>
    Object.assign(entry, changes),
  );
  persist();
};

const findCartId = (response) =>
  [
    response?.data?.shoppingCartId,
    response?.data?.cart?.shoppingCartId,
    response?.data?.shoppingCart?.shoppingCartId,
    // El backend del TPV devuelve las líneas añadidas como un array. Cada
    // línea contiene el identificador del carrito recién creado.
    response?.data?.[0]?.shoppingCartId,
    response?.data?.[0]?.ShoppingCart?.shoppingCartId,
    response?.shoppingCartId,
  ].find(Boolean) || null;

const setActiveCartBackendId = (customerId, shoppingCartId) => {
  if (!shoppingCartId) return;

  const userConfig = store.getState()?.aplicationConfig?.userConfig;
  if (
    userConfig?.order?.orderId ||
    String(userConfig?.client?.customerId) !== String(customerId)
  )
    return;
  if (
    userConfig.cart?.shoppingCartId &&
    userConfig.cart.shoppingCartId !== shoppingCartId
  )
    return;

  store.dispatch(
    updateCart({
      ...(userConfig?.cart || {}),
      shoppingCartId,
    }),
  );
};

const fetchCurrentCart = async (customerId, preferredId = null) => {
  const response = await getCustomerCarts({ customerId });
  if (!response || response.error || !Array.isArray(response.data)) return null;

  const carts = response.data;
  if (preferredId) {
    const preferred = carts.find(
      (cart) =>
        cart.state === "ACTIVE" &&
        String(cart?.shoppingCartId) === String(preferredId) &&
        String(cart.customerId) === String(customerId),
    );
    return { cart: preferred || null, carts };
  }

  const active = carts.filter((cart) => cart?.state === "ACTIVE");
  return {
    cart:
      active.find((cart) => String(cart.customerId) === String(customerId)) ||
      null,
    carts,
  };
};

const ensureCartBackendId = async (entry) => {
  if (entry.backendResourceId) return entry.backendResourceId;
  const result = await fetchCurrentCart(entry.customerId);
  const id = result?.cart?.shoppingCartId ?? null;
  if (!id) throw new Error("No se ha podido identificar el carrito creado");
  updateResourceEntries(entry.resourceKey, { backendResourceId: id });
  return id;
};

const schedule = (resourceKey, delay = SYNC_DELAY) => {
  if (paused.has(resourceKey)) return;
  const currentTimer = timers.get(resourceKey);
  if (currentTimer) window.clearTimeout(currentTimer);
  timers.set(
    resourceKey,
    window.setTimeout(() => {
      timers.delete(resourceKey);
      processResource(resourceKey);
    }, delay),
  );
};

const buildPayload = (entry, quantity) => ({
  productId: entry.productId,
  customerId: entry.customerId,
  shoppingCartId: entry.backendResourceId ?? entry.resourceId ?? null,
  variantId: entry.variantId || null,
  itemIds: Array.isArray(entry.itemIds) ? entry.itemIds : [],
  note: entry.note || "",
  ...(quantity == null ? {} : { quantity }),
});

const executeEntry = async (entry) => {
  if (entry.resourceType === "cart" && !entry.customerId)
    throw Error(
      "Este cambio no tiene un cliente válido. Revisa los cambios pendientes.",
    );
  const quantity = Number(entry.desiredQuantity) || 0;
  let response;

  if (entry.resourceType === "cart") {
    if (entry.serverExists && !entry.backendResourceId && !entry.resourceId) {
      entry.backendResourceId = await ensureCartBackendId(entry);
    }
    const payload = buildPayload(entry, quantity);
    if (!entry.serverExists && quantity > 0) {
      response = await addProductToCart(payload);
      if (
        response &&
        !response.error &&
        !entry.backendResourceId &&
        !entry.resourceId
      ) {
        const currentEntry = entries.get(entry.key);
        if (currentEntry) {
          currentEntry.serverExists = true;
          persist();
        }
        const responseCartId = findCartId(response);
        if (responseCartId) {
          updateResourceEntries(entry.resourceKey, {
            backendResourceId: responseCartId,
          });
          setActiveCartBackendId(entry.customerId, responseCartId);
        } else {
          const result = await fetchCurrentCart(entry.customerId);
          const fetchedCartId = result?.cart?.shoppingCartId ?? null;
          if (fetchedCartId) {
            updateResourceEntries(entry.resourceKey, {
              backendResourceId: fetchedCartId,
            });
            setActiveCartBackendId(entry.customerId, fetchedCartId);
          } else
            throw new Error(
              "El producto se ha guardado, pero todavía no se ha podido recuperar el carrito",
            );
        }
      }
    } else if (entry.serverExists && quantity > 0)
      response = await updateCartProductQuantity(payload);
    else if (entry.serverExists)
      response = await removeProductFromCart(buildPayload(entry, null));
    else return;
  } else {
    const baseOrderPayload = {
      note: entry.note || "",
      productId: entry.productId,
      variantId: entry.variantId || null,
      itemIds: Array.isArray(entry.itemIds) ? entry.itemIds : [],
    };
    if (!entry.serverExists && quantity > 0) {
      response = await addProductToPendingOrder(
        { ...baseOrderPayload, quantity, note: entry.note || "" },
        entry.resourceId,
      );
    } else if (entry.serverExists && quantity > 0) {
      response = await updateQuantityProductToPendingOrder(
        { ...baseOrderPayload, quantity },
        entry.resourceId,
      );
    } else if (entry.serverExists) {
      response = await removeProductToPendingOrder(
        baseOrderPayload,
        entry.resourceId,
      );
    } else return;
  }

  if (!response || response.error)
    throw new Error(
      response?.message || "No se ha podido sincronizar el producto",
    );
};

const selectedResourceMatches = (entry) => {
  const current = store.getState()?.aplicationConfig?.userConfig;
  if (entry.resourceType === "order")
    return current?.order?.orderId === entry.resourceId;
  if (
    current?.order?.orderId ||
    String(current?.client?.customerId) !== String(entry.customerId)
  )
    return false;
  const id = entry.backendResourceId || entry.resourceId;
  return id
    ? current.cart?.shoppingCartId === id
    : !current.cart?.shoppingCartId;
};

const reconcileResource = async (resourceKey, sampleEntry) => {
  if (getResourceEntries(resourceKey).length > 0) return;

  if (!selectedResourceMatches(sampleEntry)) return;
  if (sampleEntry.resourceType === "cart") {
    const cartId = sampleEntry.backendResourceId ?? sampleEntry.resourceId;
    const result = await fetchCurrentCart(sampleEntry.customerId, cartId);
    if (!result || getResourceEntries(resourceKey).length > 0) return;

    // La cola puede terminar de sincronizar después de que el operador haya
    // cambiado de cliente. En ese caso persistimos el cambio, pero no
    // sustituimos en Redux el carrito que el operador está consultando ahora.
    const selectedCustomerId =
      store.getState()?.aplicationConfig?.userConfig?.client?.customerId;
    if (
      String(selectedCustomerId) !== String(sampleEntry.customerId) ||
      !selectedResourceMatches(sampleEntry)
    )
      return;

    const { cart: currentCart, carts } = result;
    store.dispatch(
      reconcileSelectedCart({
        customerId: sampleEntry.customerId,
        expectedCartId: cartId,
        cart: currentCart,
        carts,
      }),
    );
    return;
  }

  const response = await getOrderDetails(sampleEntry.resourceId);
  if (
    !response ||
    response.error ||
    getResourceEntries(resourceKey).length > 0 ||
    !selectedResourceMatches(sampleEntry)
  )
    return;
  store.dispatch(updateOrder(response?.data?.order ?? response?.data));
  store.dispatch(updateRequestIsOrder(true));
};

const processResource = (resourceKey) => {
  if (paused.has(resourceKey)) return Promise.resolve(false);
  if (running.has(resourceKey)) return running.get(resourceKey);

  const promise = (async () => {
    let lastEntry = null;
    emit();

    while (getResourceEntries(resourceKey).length > 0) {
      const entry = getResourceEntries(resourceKey)[0];
      lastEntry = { ...entry };
      const processedVersion = entry.version;
      const processedQuantity = entry.desiredQuantity;

      try {
        await executeEntry(lastEntry);
      } catch (error) {
        if (lastEntry.resourceType === "order") {
          try {
            const check = await getOrderDetails(lastEntry.resourceId);
            const saved =
              check?.error === false ? check.data?.order || check.data : null;
            if (
              saved?.orderId === lastEntry.resourceId &&
              (saved.status !== "pending" ||
                saved.paymentStatus === "paid" ||
                (saved.products || []).some(
                  (line) => line.status !== "pending",
                ))
            ) {
              for (const queued of getResourceEntries(resourceKey))
                entries.delete(queued.key);
              errors.delete(resourceKey);
              if (
                store.getState()?.aplicationConfig?.userConfig?.order
                  ?.orderId === saved.orderId
              )
                store.dispatch(updateOrder(saved));
              store.dispatch(
                setAlert({
                  called: true,
                  type: "error",
                  title: "Pedido bloqueado",
                  message:
                    "El pedido ya no admite cambios. Se ha recuperado el estado guardado; revisa sus líneas antes de continuar.",
                }),
              );
              persist();
              emit();
              return false;
            }
          } catch {
            /* Keep pending changes on an unavailable status read. */
          }
        }
        paused.add(resourceKey);
        const alreadyReported = errors.has(resourceKey);
        errors.set(
          resourceKey,
          error?.message || "Cambios pendientes de guardar",
        );
        if (!alreadyReported) {
          store.dispatch(
            setAlert({
              called: true,
              title: "Sincronización",
              message:
                "No se pudo guardar. Los cambios se conservan: abre Revisar cambios para comprobarlos.",
            }),
          );
        }
        persist();
        emit();
        // A rejected/uncertain write must not replay forever in the background.
        return false;
      }

      lastEntry.backendResourceId =
        entries.get(entry.key)?.backendResourceId ??
        lastEntry.backendResourceId;
      const current = entries.get(entry.key);
      if (current?.version === processedVersion) entries.delete(entry.key);
      else if (current) current.serverExists = Number(processedQuantity) > 0;

      persist();
      emit();
    }

    if (lastEntry) await reconcileResource(resourceKey, lastEntry);
    errors.delete(resourceKey);
    emit();
    return true;
  })().finally(() => {
    running.delete(resourceKey);
    if (getResourceEntries(resourceKey).length > 0 && !errors.has(resourceKey))
      schedule(resourceKey, 0);
    emit();
  });

  running.set(resourceKey, promise);
  emit();
  return promise;
};

export const buildSyncResourceKey = (type, id) => (id ? `${type}:${id}` : null);

export const enqueueProductChange = ({
  resourceType,
  resourceId,
  customerId,
  lineKey,
  source,
  currentQuantity,
  delta,
  serverExists,
}) => {
  if (resourceType === "cart" && !customerId) return currentQuantity;
  const isNewCart =
    resourceType === "cart" && !resourceId && Boolean(customerId);
  const resourceKey = buildSyncResourceKey(
    resourceType,
    isNewCart ? `new-${customerId}` : resourceId,
  );
  if (!resourceKey || !lineKey) return currentQuantity;

  const key = `${resourceKey}:${lineKey}`;
  const current = entries.get(key);
  const desiredQuantity = Math.max(
    0,
    Number(current?.desiredQuantity ?? currentQuantity) + Number(delta),
  );
  const next = {
    ...(current || {}),
    key,
    resourceKey,
    resourceType,
    resourceId: isNewCart ? null : resourceId,
    backendResourceId:
      current?.backendResourceId ?? (isNewCart ? null : resourceId),
    customerId,
    lineKey,
    productId:
      source?.productId ??
      source?.Product?.productId ??
      source?.Product?.id ??
      source?.id,
    variantId: source?.variantId ?? null,
    itemIds: Array.isArray(source?.itemIds) ? source.itemIds : [],
    note: source?.note ?? "",
    source,
    desiredQuantity,
    serverExists: current ? current.serverExists : Boolean(serverExists),
    version: Number(current?.version || 0) + 1,
  };

  if (!next.serverExists && desiredQuantity === 0 && !running.has(resourceKey))
    entries.delete(key);
  else entries.set(key, next);

  if (!paused.has(resourceKey)) errors.delete(resourceKey);
  persist();
  emit();
  if (getResourceEntries(resourceKey).length > 0) schedule(resourceKey);
  return desiredQuantity;
};

export const flushProductChanges = async (resourceKey) => {
  if (!resourceKey) return true;
  if (paused.has(resourceKey)) return false;
  while (
    running.has(resourceKey) ||
    getResourceEntries(resourceKey).length > 0
  ) {
    const timer = timers.get(resourceKey);
    if (timer) window.clearTimeout(timer);
    timers.delete(resourceKey);
    errors.delete(resourceKey);

    const result = running.has(resourceKey)
      ? await running.get(resourceKey)
      : await processResource(resourceKey);
    if (!result) return false;
  }
  return true;
};

export const flushAllProductChanges = async () => {
  while (entries.size > 0 || running.size > 0) {
    const resourceKeys = new Set([
      ...Array.from(entries.values()).map((entry) => entry.resourceKey),
      ...Array.from(running.keys()),
    ]);
    const results = await Promise.all(
      Array.from(resourceKeys).map((resourceKey) =>
        flushProductChanges(resourceKey),
      ),
    );
    if (!results.every(Boolean)) return false;
  }
  return true;
};

export const resumeProductChanges = (resourceKey) => {
  if (!resourceKey || paused.has(resourceKey)) return;
  getResourceEntries(resourceKey).forEach((entry) => {
    const action =
      entry.resourceType === "cart"
        ? setOptimisticCartProductQuantity
        : setOptimisticOrderProductQuantity;
    store.dispatch(
      action({ source: entry.source, quantity: entry.desiredQuantity }),
    );
  });
  if (getResourceEntries(resourceKey).length > 0) schedule(resourceKey);
};

export const hasPendingProductChanges = (resourceKey) =>
  Boolean(
    resourceKey &&
    (getResourceEntries(resourceKey).length > 0 || running.has(resourceKey)),
  );

export const hasAnyPendingProductChanges = () =>
  entries.size > 0 || running.size > 0;

export const getProductSyncStatus = (resourceKey) => ({
  paused: paused.has(resourceKey),
  pendingCount: resourceKey ? getResourceEntries(resourceKey).length : 0,
  syncing: Boolean(resourceKey && running.has(resourceKey)),
  error: resourceKey ? errors.get(resourceKey) || null : null,
});

export const subscribeProductSync = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getProductSyncSnapshotVersion = () => snapshotVersion;

export const getPausedProductChanges = () =>
  Array.from(paused)
    .map((resourceKey) => ({
      resourceKey,
      message: errors.get(resourceKey),
      running: running.has(resourceKey),
      lines: getResourceEntries(resourceKey).map((entry) => ({
        key: entry.key,
        name:
          entry.source?.Product?.shortName ||
          entry.source?.Product?.name ||
          entry.source?.name ||
          "Producto",
        quantity: entry.desiredQuantity,
      })),
    }))
    .filter((group) => group.lines.length);

const matchesLine = (entry, line) =>
  String(entry.productId) ===
    String(line.productId ?? line.Product?.productId ?? line.Product?.id) &&
  String(entry.variantId || "") === String(line.variantId || "") &&
  JSON.stringify([...(entry.itemIds || [])].map(String).sort()) ===
    JSON.stringify([...(line.itemIds || [])].map(String).sort()) &&
  String(entry.note || "").trim() === String(line.note || "").trim();

export const retryPausedProductChanges = async (resourceKey) => {
  if (running.has(resourceKey)) throw Error("La comprobación sigue en curso.");
  const pending = getResourceEntries(resourceKey);
  if (!pending.length) return true;
  const sample = pending[0];
  const id = sample.backendResourceId || sample.resourceId;
  // An old request without an acknowledged basket ID cannot safely be sent again.
  if (sample.resourceType === "cart" && (!id || !sample.customerId))
    throw Error(
      "No se puede identificar la cesta de estos cambios. Revisa el historial antes de descartarlos del dispositivo; no se enviarán a otra cesta.",
    );
  let lines;
  if (sample.resourceType === "cart") {
    const saved = await fetchCurrentCart(sample.customerId, id);
    if (!saved?.cart)
      throw Error(
        "La cesta original ya no está activa o no se pudo leer. Revisa el historial antes de descartar los cambios del dispositivo.",
      );
    lines = saved.cart.ShoppingCartProducts || [];
  } else {
    const response = await getOrderDetails(id),
      order = response?.data?.order || response?.data;
    if (response?.error !== false || order?.orderId !== id)
      throw Error("No se pudo comprobar el pedido original.");
    if (
      order.status !== "pending" ||
      order.paymentStatus !== "pending" ||
      (order.products || []).some((line) => line.status !== "pending")
    )
      throw Error(
        "El pedido original ya no admite cambios. Revisa su detalle antes de descartar los cambios del dispositivo.",
      );
    lines = order.products || [];
  }
  for (const entry of pending) {
    if (entries.get(entry.key) !== entry)
      throw Error("Los cambios han variado. Vuelve a revisarlos.");
    const saved = lines.find((line) => matchesLine(entry, line));
    if (Number(saved?.quantity || 0) === Number(entry.desiredQuantity))
      entries.delete(entry.key);
    else entry.serverExists = Boolean(saved);
  }
  paused.delete(resourceKey);
  errors.delete(resourceKey);
  persist();
  emit();
  if (!getResourceEntries(resourceKey).length) {
    await reconcileResource(resourceKey, sample);
    return true;
  }
  return flushProductChanges(resourceKey);
};

// Only the explicitly reviewed local requests are removed. Never writes server data.
export const discardPausedProductChanges = async (resourceKey) => {
  if (running.has(resourceKey))
    throw Error("Espera a que termine la comprobación.");
  const pending = getResourceEntries(resourceKey);
  const sample = pending[0];
  if (!sample) return;
  if (selectedResourceMatches(sample)) {
    // Preserve visible unsaved work if its server state cannot be read.
    if (sample.resourceType === "cart") {
      const originalId = sample.backendResourceId || sample.resourceId;
      // Without an acknowledged ID, discard only the local draft; never choose another active basket.
      const saved = originalId
        ? await fetchCurrentCart(sample.customerId, originalId)
        : { cart: null };
      if (!saved)
        throw Error(
          "No se pudo leer la cesta. Los cambios siguen conservados.",
        );
      if (selectedResourceMatches(sample))
        store.dispatch(
          updateCart(
            saved.cart || {
              customerId: sample.customerId,
              ShoppingCartProducts: [],
              total: 0,
            },
          ),
        );
    } else {
      const response = await getOrderDetails(sample.resourceId),
        order = response?.data?.order || response?.data;
      if (response?.error !== false || order?.orderId !== sample.resourceId)
        throw Error(
          "No se pudo leer el pedido. Los cambios siguen conservados.",
        );
      if (selectedResourceMatches(sample)) store.dispatch(updateOrder(order));
    }
  }
  for (const entry of pending)
    if (entries.get(entry.key) !== entry)
      throw Error("Los cambios han variado. Vuelve a revisarlos.");
  pending.forEach((entry) => entries.delete(entry.key));
  const timer = timers.get(resourceKey);
  if (timer) window.clearTimeout(timer);
  timers.delete(resourceKey);
  paused.delete(resourceKey);
  errors.delete(resourceKey);
  persist();
  emit();
};
